import { Component, OnInit, ViewChild } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, Validators } from '@angular/forms';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTable, MatTableDataSource } from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { NotificationService } from 'src/app/notifications/notification.service';
import { SessionService } from 'src/app/session.service';
import { UiService } from 'src/app/ui/ui.service';
import { pageSizeOptions } from 'src/app/shared/utils/pagination';
import { IntegrationsService } from '../../integrations/integrations.service';
import { LocalStorageService } from 'src/app/storage/local-storage.service';
import { UtilService } from 'src/app/util/util.service';
import { FileSaverService } from 'ngx-filesaver';
import moment from 'moment-timezone';
import { getAssististDocumentLabels } from '../document-labels';

@Component({
  selector: 'app-assistist-usage-report',
  templateUrl: './integration-usage-report.component.html',
  styleUrls: ['./integration-usage-report.component.scss']
})
export class IntegrationUsageReportComponent implements OnInit {
  pageSizeOptions = pageSizeOptions;
  filterForm!: FormGroup;
  displayedColumns = [
    'date',
    'results_count',
    'threads',
    'anonymous_threads',
    'non_anonymous_threads',
    'unique_devices',
    'unique_users',
    'total_shares',
    'total_ratings',
    'total_feedback',
    'average_rating'
  ];
  usageData: any[] = [];
  usageDataSource!: MatTableDataSource<any>;
  @ViewChild('usageTable') usageTable!: MatTable<any[]>;
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  
  private onComponentDestroy = new Subject();
  perPage: number = 20;
  integrationId!: string;
  integration: any = null;
  featureType: string = ''; // 'tripist' | 'explorist' | 'aura'
  includeTests: boolean = false;
  topCategories: Array<{ category_id: number | null; category_name: string; returned_count: number }> = [];
  topDocumentsByCategory: Record<string, Array<{ document_id: number; name: string; subtitle?: string; returned_count: number }>> = {};
  topCategoryDailyCounts: Array<{ date: string; category_id: number; returned_count: number }> = [];
  selectedCategoryId: number | null = null;
  userTimezone: string = 'UTC';

  get documentsLabelPlural(): string {
    return getAssististDocumentLabels(this.featureType).plural;
  }

  get isAura(): boolean {
    return this.featureType === 'aura';
  }

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private integrationsService: IntegrationsService,
    private sessionService: SessionService,
    private notificationService: NotificationService,
    private uiService: UiService,
    private utilSvc: UtilService,
    private localStorageSvc: LocalStorageService,
    private fileSaver: FileSaverService,
  ) {}

  ngOnInit(): void {
    this.perPage = this.localStorageSvc.pageSize || 20;
    this.featureType = this.route.snapshot.params['featureType'];
    this.includeTests = this.parseIncludeTestsQuery(this.route.snapshot.queryParams['include_tests']);
    if (this.isAura) {
      this.displayedColumns = [
        'date',
        'threads',
        'bookings_created',
        'bookings_updated',
        'bookings_cancelled',
        'availability_checks',
        'bookings_queries',
      ];
    }
    this.integrationId = this.route.snapshot.params['integrationId'];
    this.integration = this.route.snapshot.data.integration;
    this.userTimezone = this.sessionService.currentUserValue?.timezone || 'UTC';

    // Use integration ID from resolved data if route param is not available
    if (!this.integrationId && this.integration?.id) {
      this.integrationId = this.integration.id.toString();
    }

    // Get dates from URL parameters or use defaults (last 7 days)
    let startDate: Date, endDate: Date;
    
    const today = moment.tz(this.userTimezone).endOf('day').toDate();
    
    if (this.route.snapshot.queryParams['start_date'] && this.route.snapshot.queryParams['end_date']) {
      const urlStartDate = this.parseDateFromQuery(this.route.snapshot.queryParams['start_date']);
      const urlEndDate = this.parseDateFromQuery(this.route.snapshot.queryParams['end_date']);
      
      // Validate dates are not in the future
      if (urlStartDate && urlEndDate && urlStartDate <= today && urlEndDate <= today) {
        startDate = urlStartDate;
        endDate = urlEndDate;
             } else {
         // Invalid dates in URL, use defaults and clear URL params  
         endDate = moment.tz(this.userTimezone).toDate(); // Today's date
         startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
         
         // Clear invalid URL parameters
         this.router.navigate([], {
           relativeTo: this.route,
           queryParams: {
             start_date: this.formatDateForQuery(startDate),
             end_date: this.formatDateForQuery(endDate)
           },
           queryParamsHandling: 'merge',
           replaceUrl: true
         });
       }
          } else {
      // Default to last 7 days in user's timezone (ending today)
      endDate = moment.tz(this.userTimezone).toDate(); // Today's date
      startDate = moment.tz(this.userTimezone).subtract(6, 'days').startOf('day').toDate();
    }

    // Check if we're coming from feedback with dates
    const feedbackStartDate = this.route.snapshot.queryParams['feedback_start_date'];
    const feedbackEndDate = this.route.snapshot.queryParams['feedback_end_date'];
    
    if (feedbackStartDate && feedbackEndDate) {
      const feedbackStart = this.parseDateFromQuery(feedbackStartDate);
      const feedbackEnd = this.parseDateFromQuery(feedbackEndDate);
      
      // Validate dates are not in the future
      if (feedbackStart && feedbackEnd && feedbackStart <= today && feedbackEnd <= today) {
        startDate = feedbackStart;
        endDate = feedbackEnd;
        
        // Convert feedback dates to usage dates and update URL
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: {
            start_date: this.formatDateForQuery(startDate),
            end_date: this.formatDateForQuery(endDate)
          },
          queryParamsHandling: 'merge',
          replaceUrl: true
        });
      }
    }

    // Create validator for dates not in the future
    const maxDateValidator = (control: AbstractControl) => {
      if (!control.value) return null;
      const selectedDate = new Date(control.value);
      selectedDate.setHours(0, 0, 0, 0); // Start of selected day
      
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Start of today
      
      return selectedDate > today ? { futureDate: true } : null;
    };

    this.filterForm = new FormGroup({
      startDate: new FormControl(startDate, [Validators.required, maxDateValidator]),
      endDate: new FormControl(endDate, [Validators.required, maxDateValidator]),
    });

    this.usageDataSource = new MatTableDataSource<any>([]);

    // Subscribe to query params changes
    this.route.queryParams
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(params => {
        const includeTestsFromUrl = this.parseIncludeTestsQuery(params['include_tests']);
        const includeTestsChanged = includeTestsFromUrl !== this.includeTests;
        if (includeTestsChanged) {
          this.includeTests = includeTestsFromUrl;
        }

        let shouldReload = includeTestsChanged;
        if (params['start_date'] && params['end_date']) {
          const urlStartDate = this.parseDateFromQuery(params['start_date']);
          const urlEndDate = this.parseDateFromQuery(params['end_date']);
          
          // Only update if dates are different to avoid infinite loop
          if (
            urlStartDate && urlEndDate &&
            (
              urlStartDate.getTime() !== this.filterForm.controls.startDate.value?.getTime() ||
              urlEndDate.getTime() !== this.filterForm.controls.endDate.value?.getTime()
            )
          ) {
            this.filterForm.setValue({
              startDate: urlStartDate,
              endDate: urlEndDate
            }, { emitEvent: false });
            shouldReload = true;
          }
        }
        if (shouldReload) {
          this.loadUsageReport();
        }
      });

    this.filterForm.valueChanges
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe((change) => {
        this.onFilterFormChange(change);
      });

    // Load initial data
    if (this.integrationId) {
      this.loadUsageReport();
    } else {
      this.notificationService.openErrorSnackBar('Integration ID not found');
    }
  }

  ngAfterViewInit() {
    this.usageDataSource.paginator = this.paginator;
    this.usageDataSource.sort = this.sort;
  }

  ngOnDestroy() {
    this.onComponentDestroy.next();
    this.onComponentDestroy.complete();
  }

  formatDate = (date: Date) => {
    // For usage reports, display the raw date without timezone conversion
    return moment(date).format("MMM DD YYYY");
  }

  onFilterFormChange(change: any) {
    // Only proceed if form is valid (no future dates)
    if (!this.filterForm.valid) {
      return;
    }
    
    // Update URL with new date parameters
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        start_date: this.formatDateForQuery(change.startDate),
        end_date: this.formatDateForQuery(change.endDate),
        include_tests: this.includeTests
      },
      queryParamsHandling: 'merge'
    });
    
    this.loadUsageReport();
  }

  onIncludeTestsToggle(checked: boolean): void {
    if (this.includeTests === checked) {
      return;
    }

    this.includeTests = checked;
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        include_tests: this.includeTests
      },
      queryParamsHandling: 'merge'
    });

    this.loadUsageReport();
  }

  loadUsageReport() {
    if (!this.integrationId) {
      this.notificationService.openErrorSnackBar('Integration ID is required');
      return;
    }

    // Convert selected dates to UTC for API call
    const selectedStartDate = this.filterForm.controls.startDate.value;
    const selectedEndDate = this.filterForm.controls.endDate.value;
    
    // Create dates using simple ISO date strings for the calendar dates
    // This ensures we get the exact calendar days regardless of timezone
    const startDateStr = selectedStartDate.getFullYear() + '-' + 
                        String(selectedStartDate.getMonth() + 1).padStart(2, '0') + '-' + 
                        String(selectedStartDate.getDate()).padStart(2, '0');
    const endDateStr = selectedEndDate.getFullYear() + '-' + 
                      String(selectedEndDate.getMonth() + 1).padStart(2, '0') + '-' + 
                      String(selectedEndDate.getDate()).padStart(2, '0');
    
    const startDate = startDateStr + 'T00:00:00.000Z';
    const endDate = endDateStr + 'T23:59:59.999Z';

    this.uiService
      .waitForEvent(
        this.integrationsService.getIntegrationUsageReport(
          this.sessionService.currentAccountValue!.accountID,
          this.integrationId,
          {
            start_date: startDate,
            end_date: endDate,
            include_tests: this.includeTests
          }
        )
      )
      .pipe(takeUntil(this.onComponentDestroy))
      .subscribe(
        (res) => {
          this.usageData = res.data || [];
          this.usageDataSource = new MatTableDataSource(this.usageData);
          this.usageDataSource.paginator = this.paginator;
          this.usageDataSource.sort = this.sort;

          this.topCategories = res.top_categories || [];
          this.topDocumentsByCategory = res.top_documents_by_category || {};
          this.topCategoryDailyCounts = res.top_category_daily_counts || [];
          if (this.topCategories.length > 0 && this.selectedCategoryId === null) {
            // Default selection to the most popular category
            const first = this.topCategories[0];
            this.selectedCategoryId = (first && first.category_id != null) ? first.category_id : null;
          }
        },
        () => {
          this.notificationService.openErrorSnackBar(
            'Error retrieving usage report'
          );
        }
      );
  }

  selectCategory(categoryId: number | null) {
    this.selectedCategoryId = categoryId;
  }

  getSelectedCategoryDocuments() {
    if (this.selectedCategoryId === null || this.selectedCategoryId === undefined) {
      return [];
    }
    return this.topDocumentsByCategory[String(this.selectedCategoryId)] || [];
  }

  onPageChange(e: PageEvent) {
    this.localStorageSvc.pageSize = e.pageSize;
    this.perPage = e.pageSize;
  }

  getTotalThreads(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.threads) || 0), 0) || 0;
  }

  getTotalResults(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.results_count) || 0), 0) || 0;
  }

  getTotalAnonymousThreads(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.anonymous_threads) || 0), 0) || 0;
  }

  getTotalNonAnonymousThreads(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.non_anonymous_threads) || 0), 0) || 0;
  }

  getTotalUniqueDevices(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.unique_devices) || 0), 0) || 0;
  }

  getTotalUniqueUsers(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.unique_users) || 0), 0) || 0;
  }

  getTotalShares(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.total_shares) || 0), 0) || 0;
  }

  getTotalRatings(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.total_ratings) || 0), 0) || 0;
  }

  getTotalFeedback(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.total_feedback) || 0), 0) || 0;
  }

  getAverageRating(): number {
    if (!this.usageData?.length) return 0;
    
    const totalRating = this.usageData.reduce((sum, item) => {
      const rating = Number(item.average_rating) || 0;
      const count = Number(item.total_ratings) || 0;
      return sum + (rating * count);
    }, 0);
    
    const totalRatings = this.getTotalRatings();
    return totalRatings > 0 ? totalRating / totalRatings : 0;
  }

  getTotalBookingsCreated(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.bookings_created) || 0), 0) || 0;
  }

  getTotalBookingsUpdated(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.bookings_updated) || 0), 0) || 0;
  }

  getTotalBookingsCancelled(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.bookings_cancelled) || 0), 0) || 0;
  }

  getTotalAvailabilityChecks(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.availability_checks) || 0), 0) || 0;
  }

  getTotalBookingsQueries(): number {
    return this.usageData?.reduce((total, item) => total + (Number(item.bookings_queries) || 0), 0) || 0;
  }

  navigateToFeedback() {
    const startDate = this.filterForm.controls.startDate.value;
    const endDate = this.filterForm.controls.endDate.value;
    
    this.router.navigate(['/assistist', this.route.snapshot.params['featureType'], this.integrationId, 'feedback', 'list'], { 
      queryParams: {
        usage_start_date: this.formatDateForQuery(startDate),
        usage_end_date: this.formatDateForQuery(endDate),
        include_tests: this.includeTests
      }
    });
  }

  private parseIncludeTestsQuery(value: any): boolean {
    if (typeof value === 'boolean') {
      return value;
    }
    if (typeof value === 'number') {
      return value === 1;
    }
    if (typeof value === 'string') {
      return ['1', 'true', 'yes', 'y', 'on'].includes(value.trim().toLowerCase());
    }
    return false;
  }

  private parseDateFromQuery(dateString: string): Date | null {
    if (!dateString || typeof dateString !== 'string') {
      return null;
    }

    const parts = dateString.split('-').map(Number);
    if (parts.length !== 3 || parts.some((part) => Number.isNaN(part))) {
      return null;
    }

    const [year, month, day] = parts;
    const date = new Date(year, month - 1, day);
    // Guard against invalid dates like 2026-02-31
    if (
      date.getFullYear() !== year ||
      date.getMonth() !== month - 1 ||
      date.getDate() !== day
    ) {
      return null;
    }

    return date;
  }

  private formatDateForQuery(date: Date): string {
    return [
      date.getFullYear(),
      String(date.getMonth() + 1).padStart(2, '0'),
      String(date.getDate()).padStart(2, '0')
    ].join('-');
  }

  exportToExcel() {
    this.exportUsageData('xls');
  }

  exportToCsv() {
    this.exportUsageData('csv');
  }

  private exportUsageData(format: 'xls' | 'csv') {
    if (!this.usageData?.length) {
      this.notificationService.openErrorSnackBar('No usage data available to export');
      return;
    }

    const { headers, rows } = this.buildExportRows();
    const filename = moment().format(
      format === 'xls'
        ? '[LHPEUsageReport-]YYYY-MM-DD-HH-mm-ss[.xls]'
        : '[LHPEUsageReport-]YYYY-MM-DD-HH-mm-ss[.csv]'
    );

    if (format === 'csv') {
      const csvContent = [
        headers.map((value) => this.escapeCsvValue(value)).join(','),
        ...rows.map((row) => row.map((value) => this.escapeCsvValue(value)).join(',')),
      ].join('\n');

      this.fileSaver.save(
        new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }),
        filename
      );
      return;
    }

    const tableRows = rows
      .map((row) => `<tr>${row.map((value) => `<td>${this.escapeHtml(value)}</td>`).join('')}</tr>`)
      .join('');
    const html = `<table><thead><tr>${headers.map((h) => `<th>${this.escapeHtml(h)}</th>`).join('')}</tr></thead><tbody>${tableRows}</tbody></table>`;

    this.fileSaver.save(
      new Blob([html], { type: 'application/vnd.ms-excel;charset=utf-8;' }),
      filename
    );
  }

  private buildExportRows(): { headers: string[]; rows: Array<Array<string | number>> } {
    const baseColumns: Array<{ key: string; label: string }> = [
      { key: 'date', label: 'Date' },
      { key: 'results_count', label: 'Results' },
      { key: 'threads', label: 'Threads' },
      { key: 'anonymous_threads', label: 'Anonymous' },
      { key: 'non_anonymous_threads', label: 'Non-Anonymous' },
      { key: 'unique_devices', label: 'Unique Devices' },
      { key: 'unique_users', label: 'Unique Users' },
      { key: 'total_shares', label: 'Shares' },
      { key: 'total_ratings', label: 'Ratings' },
      { key: 'total_feedback', label: 'Feedback' },
      { key: 'average_rating', label: 'Avg Rating' },
    ];
    const categoryColumns = (this.topCategories || []).filter((category) => category?.category_id != null);
    const dailyCountsByDate = this.getDailyCategoryCountsMap();

    const headers = [
      ...baseColumns.map((column) => column.label),
      ...categoryColumns.map((category) => category.category_name),
    ];

    const rows = (this.usageData || []).map((row: any) => {
      const dateKey = this.normalizeDateKey(row.date);
      const categoryCountsForDate = dailyCountsByDate[dateKey] || {};

      const baseValues = baseColumns.map((column) => {
        if (column.key === 'date') {
          return this.formatDate(row.date);
        }
        if (column.key === 'average_rating') {
          return Number(row.average_rating || 0).toFixed(1);
        }
        return Number(row[column.key] || 0);
      });

      const categoryValues = categoryColumns.map((category) => {
        return Number(categoryCountsForDate[String(category.category_id)] || 0);
      });

      return [...baseValues, ...categoryValues];
    });

    return { headers, rows };
  }

  private getDailyCategoryCountsMap(): Record<string, Record<string, number>> {
    return (this.topCategoryDailyCounts || []).reduce((acc, item) => {
      const dateKey = this.normalizeDateKey(item.date);
      const categoryId = String(item.category_id);

      if (!acc[dateKey]) {
        acc[dateKey] = {};
      }
      acc[dateKey][categoryId] = Number(item.returned_count || 0);

      return acc;
    }, {} as Record<string, Record<string, number>>);
  }

  private normalizeDateKey(date: string | Date): string {
    return moment(date).format('YYYY-MM-DD');
  }

  private escapeCsvValue(value: string | number): string {
    const str = String(value ?? '');
    const escaped = str.replace(/"/g, '""');
    return `"${escaped}"`;
  }

  private escapeHtml(value: string | number): string {
    return String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }
} 